<template>
  <div>
    <headerSection></headerSection>
    <div id="main__content">
      <div class="x-main-container">
        <div class="x-main-content">
          <div class="worldcup-page" :style="worldCupThemeStyle">

            <!-- Hero -->
            <div class="wc-hero">
              <div class="wc-hero-bg"></div>
              <div class="wc-pitch"></div>
              <div class="wc-glow wc-glow-1"></div>
              <div class="wc-glow wc-glow-2"></div>
              <div class="wc-hero-content">
                <div class="wc-hero-icon"><span class="hero-ball">⚽</span><span class="hero-trophy">🏆</span></div>
                <h1 class="wc-hero-title">ทายผลบอลโลก 2026</h1>
                <div class="wc-hero-sub">FIFA WORLD CUP 2026 · 11 มิ.ย. – 19 ก.ค. · 104 นัด</div>
                <div class="wc-my-points" @click="$router.push('/worldcup-ranking')">
                  <span class="pts-label">แต้มสะสมของฉัน</span>
                  <span class="pts-value">{{ myTotalPoints.toLocaleString() }}</span>
                  <span class="pts-link">ดูอันดับ + รับรางวัล ›</span>
                </div>
                <div class="wc-rules">
                  <div class="rule-chip">⚽ ทายผลถูก +1 แต้ม</div>
                  <div class="rule-chip">🎯 ทายสกอร์เป๊ะ +3 แต้ม</div>
                  <div class="rule-chip">💰 ฝาก 250 วันนี้ ทายบอลพรุ่งนี้ได้เลย</div>
                </div>
              </div>
            </div>

            <!-- Loading -->
            <div v-if="loading" class="wc-loading">
              <div class="loading-spinner"></div>
              <span>กำลังโหลด...</span>
            </div>

            <div v-else-if="matchDates.length === 0" class="wc-empty">
              <div class="empty-icon">📅</div>
              <div class="empty-text">ยังไม่มีคู่บอลเปิดให้ทาย</div>
              <div class="empty-sub">กลับมาใหม่เร็วๆ นี้!</div>
            </div>

            <template v-else>
              <!-- Day tabs -->
              <div class="day-tabs">
                <button
                  v-for="d in matchDates"
                  :key="d"
                  class="day-tab"
                  :class="{ active: d === selectedDate }"
                  @click="selectDate(d)"
                >
                  <span class="tab-day">{{ formatTabDay(d) }}</span>
                  <span class="tab-date">{{ formatTabDate(d) }}</span>
                </button>
              </div>

              <!-- Eligibility banner -->
              <div v-if="eligLoading" class="elig-banner elig-loading">
                <span>กำลังตรวจสอบสิทธิ์...</span>
              </div>
              <div v-else-if="elig && elig.eligible" class="elig-banner elig-ok">
                ✅ มีสิทธิ์ทายชุดนี้ (ฝากวันที่ {{ formatThaiDate(elig.checkedDate) }} ครบ {{ elig.required }} บาท)
              </div>
              <div v-else-if="elig && !elig.eligible" class="elig-banner elig-no">
                <div class="elig-title">🔒 ยังไม่มีสิทธิ์ทายชุดวันที่ {{ formatThaiDate(selectedDate) }}</div>
                <div class="elig-desc">
                  ต้องมียอดฝากวันที่ <strong>{{ formatThaiDate(elig.checkedDate) }}</strong> อย่างน้อย <strong>{{ elig.required }}</strong> บาท
                  · ของคุณ: <strong>{{ elig.totalDeposit.toLocaleString() }}</strong> บาท
                </div>
                <div v-if="isCheckedDateToday" class="elig-actions">
                  <button class="btn-deposit" @click="$router.push('/')">ฝากเงินเลย</button>
                  <button class="btn-recheck" @click="loadEligibility(selectedDate, true)">เช็คสิทธิ์อีกครั้ง</button>
                </div>
                <div v-else-if="isCheckedDateFuture" class="elig-expired">ฝากครบ {{ elig.required }} บาทในวันที่ {{ formatThaiDate(elig.checkedDate) }} แล้วกลับมาทายชุดนี้ได้เลย</div>
                <div v-else class="elig-expired">หมดเวลาฝากสำหรับชุดนี้แล้ว — ยังดูผลและทายชุดถัดไปได้</div>
              </div>

              <!-- Match cards -->
              <div class="match-list">
                <div v-for="m in matchesOfDay" :key="m.id" class="match-card" :class="{ 'card-finished': m.status === 'finished', 'card-locked': isLocked(m) && m.status !== 'finished' }">
                  <div class="match-head">
                    <span class="match-round">{{ m.roundLabel }}</span>
                    <span class="match-time">
                      <template v-if="m.status === 'finished'">จบแล้ว</template>
                      <template v-else-if="isLocked(m)">🔒 ปิดรับคำทาย/รอผล</template>
                      <template v-else>⏰ เตะ {{ formatKickoff(m.kickoff) }}</template>
                    </span>
                  </div>

                  <div class="match-teams">
                    <div class="team team-home">
                      <div class="team-flag">{{ m.flag1 || '🏳️' }}</div>
                      <div class="team-name">{{ m.team1 }}</div>
                    </div>
                    <div class="match-center">
                      <template v-if="m.status === 'finished'">
                        <div class="final-score">{{ m.score1 }} - {{ m.score2 }}</div>
                      </template>
                      <template v-else>
                        <div class="vs-text">VS</div>
                      </template>
                    </div>
                    <div class="team team-away">
                      <div class="team-flag">{{ m.flag2 || '🏳️' }}</div>
                      <div class="team-name">{{ m.team2 }}</div>
                    </div>
                  </div>

                  <!-- Finished: show my result -->
                  <div v-if="m.status === 'finished'" class="my-result">
                    <template v-if="myPredictions[m.id]">
                      <span class="my-pick-text">คุณทาย: {{ pickLabel(m, myPredictions[m.id].resultPick) }} ({{ myPredictions[m.id].scoreHome }}-{{ myPredictions[m.id].scoreAway }})</span>
                      <span class="points-chip" :class="pointsClass(myPredictions[m.id].pointsAwarded)">
                        {{ myPredictions[m.id].scored ? '+' + (myPredictions[m.id].pointsAwarded || 0) + ' แต้ม' : 'รอคิดแต้ม' }}
                      </span>
                    </template>
                    <span v-else class="no-pick">ไม่ได้ทายคู่นี้</span>
                  </div>

                  <!-- Locked (prediction closed, no result yet): show my pick -->
                  <div v-else-if="isLocked(m)" class="my-result">
                    <template v-if="myPredictions[m.id]">
                      <span class="my-pick-text">คุณทาย: {{ pickLabel(m, myPredictions[m.id].resultPick) }} ({{ myPredictions[m.id].scoreHome }}-{{ myPredictions[m.id].scoreAway }})</span>
                      <span class="points-chip chip-wait">รอผล</span>
                    </template>
                    <span v-else class="no-pick">ไม่ได้ทายคู่นี้</span>
                  </div>

                  <!-- Open: pick UI -->
                  <div v-else class="pick-area" :class="{ 'pick-disabled': !canPredict }">
                    <div class="pick-row">
                      <button class="pick-btn" :class="{ selected: drafts[m.id] && drafts[m.id].resultPick === '1' }" @click="setPick(m, '1')">
                        {{ shortName(m.team1) }} ชนะ
                      </button>
                      <button class="pick-btn pick-draw" :class="{ selected: drafts[m.id] && drafts[m.id].resultPick === 'X' }" @click="setPick(m, 'X')">
                        เสมอ
                      </button>
                      <button class="pick-btn" :class="{ selected: drafts[m.id] && drafts[m.id].resultPick === '2' }" @click="setPick(m, '2')">
                        {{ shortName(m.team2) }} ชนะ
                      </button>
                    </div>
                    <div class="score-row" v-if="drafts[m.id] && drafts[m.id].resultPick">
                      <span class="score-label">ทายสกอร์:</span>
                      <div class="stepper">
                        <button class="step-btn" @click="stepScore(m, 'scoreHome', -1)">−</button>
                        <span class="step-val">{{ drafts[m.id].scoreHome }}</span>
                        <button class="step-btn" @click="stepScore(m, 'scoreHome', 1)">+</button>
                      </div>
                      <span class="score-dash">-</span>
                      <div class="stepper">
                        <button class="step-btn" @click="stepScore(m, 'scoreAway', -1)">−</button>
                        <span class="step-val">{{ drafts[m.id].scoreAway }}</span>
                        <button class="step-btn" @click="stepScore(m, 'scoreAway', 1)">+</button>
                      </div>
                      <span v-if="isContradiction(m)" class="contra-warn">⚠️ สกอร์ไม่ตรงกับผลที่เลือก</span>
                    </div>
                    <div v-if="myPredictions[m.id] && !isDirty(m)" class="saved-note">✅ บันทึกแล้ว (แก้ได้ถึง 23:59 ของวันก่อนแข่ง)</div>
                  </div>
                </div>
              </div>

              <!-- Save bar -->
              <div v-if="dirtyMatchIds.length > 0 && canPredict" class="save-bar">
                <button class="btn-save" @click="submitPicks" :disabled="submitting">
                  {{ submitting ? 'กำลังบันทึก...' : `บันทึกคำทาย (${dirtyMatchIds.length} คู่)` }}
                </button>
              </div>
            </template>

            <div class="wc-footer">
              <button class="btn-back" @click="$router.push('/')">← กลับหน้าหลัก</button>
            </div>
          </div>
        </div>
      </div>
      <footerSection></footerSection>
    </div>
  </div>
</template>

<script>
import firebase from 'firebase/app'
import 'firebase/firestore'
import 'firebase/database'
import 'firebase/auth'
import 'firebase/functions'
import router from './../router'
import moment from 'moment-timezone'

export default {
  name: 'WorldCupPredictor',
  components: {
    headerSection: () => import('./Header.vue'),
    footerSection: () => import('./Footer.vue')
  },
  created () {
    const user = localStorage.getItem('userData')
    if (user) {
      const _user = JSON.parse(user)
      this.$store.dispatch('user/setUser', _user).then(() => {
        this.loadAll()
      }).catch(err => { console.error(err) })
    } else {
      firebase.auth().signOut()
      localStorage.removeItem('userData')
      router.replace('/login')
    }
  },
  mounted () {
    this.backgroundImage = require('../assets/images/Shiba-BG.jpg')
    this.tickTimer = setInterval(() => { this.nowStr = moment().tz('Asia/Bangkok').format('YYYY-MM-DD HH:mm:ss') }, 15000)
  },
  beforeDestroy () {
    if (this.tickTimer) clearInterval(this.tickTimer)
  },
  computed: {
    worldCupThemeStyle () {
      return {
        'background-image': `url(${this.backgroundImage})`,
        'background-position': 'center',
        'background-repeat': 'no-repeat',
        'background-size': 'cover',
        '--wc-primary': '#f2c94c',
        '--wc-secondary': '#1856b3',
        '--wc-accent': '#fff4bf',
        '--wc-deep': '#10183f',
        '--wc-primary-rgb': '242, 201, 76',
        '--wc-secondary-rgb': '24, 86, 179'
      }
    },
    user () {
      return this.$store.state.user.user
    },
    matchDates () {
      const dates = []
      this.matches.forEach((m) => { if (!dates.includes(m.matchDate)) dates.push(m.matchDate) })
      dates.sort()
      return dates
    },
    matchesOfDay () {
      return this.matches.filter(m => m.matchDate === this.selectedDate)
    },
    elig () {
      return this.eligByDate[this.selectedDate] || null
    },
    canPredict () {
      return !!(this.elig && this.elig.eligible)
    },
    isCheckedDateToday () {
      if (!this.elig) return false
      return this.elig.checkedDate === this.nowStr.substring(0, 10)
    },
    isCheckedDateFuture () {
      if (!this.elig) return false
      return this.elig.checkedDate > this.nowStr.substring(0, 10)
    },
    myTotalPoints () {
      return (this.myTotal && this.myTotal.totalPoints) ? this.myTotal.totalPoints : 0
    },
    dirtyMatchIds () {
      return this.matchesOfDay
        .filter(m => m.status !== 'finished' && !this.isLocked(m) && this.isDirty(m))
        .map(m => m.id)
    }
  },
  data () {
    return {
      backgroundImage: '',
      loading: true,
      submitting: false,
      eligLoading: false,
      matches: [],
      myPredictions: {},
      myTotal: null,
      drafts: {},
      eligByDate: {},
      selectedDate: '',
      nowStr: moment().tz('Asia/Bangkok').format('YYYY-MM-DD HH:mm:ss'),
      tickTimer: null
    }
  },
  methods: {
    async loadAll () {
      this.loading = true
      try {
        const matchSnap = await firebase.firestore().collection('worldcup_matches')
          .where('published', '==', true).get()
        const matches = []
        matchSnap.forEach((doc) => { matches.push({ id: doc.id, ...doc.data() }) })
        matches.sort((a, b) => (a.kickoff < b.kickoff ? -1 : 1))
        this.matches = matches

        const predSnap = await firebase.firestore().collection('worldcup_predictions')
          .where('userkey', '==', this.user.key).get()
        const preds = {}
        predSnap.forEach((doc) => { const p = doc.data(); preds[p.matchId] = p })
        this.myPredictions = preds

        const totalDoc = await firebase.firestore().collection('worldcup_predictions_total').doc(this.user.key).get()
        this.myTotal = totalDoc.exists ? totalDoc.data() : null

        this.initDrafts()

        const now = this.nowStr
        let defaultDate = ''
        for (const d of this.matchDates) {
          if (this.matches.some(m => m.matchDate === d && !this.isLocked(m) && m.status !== 'finished')) { defaultDate = d; break }
        }
        if (!defaultDate && this.matchDates.length) defaultDate = this.matchDates[this.matchDates.length - 1]
        if (defaultDate) {
          this.selectedDate = defaultDate
          this.loadEligibility(defaultDate)
        }
      } catch (err) {
        console.error(err)
      } finally {
        this.loading = false
      }
    },
    initDrafts () {
      const drafts = {}
      this.matches.forEach((m) => {
        const p = this.myPredictions[m.id]
        drafts[m.id] = p
          ? { resultPick: p.resultPick, scoreHome: Number(p.scoreHome), scoreAway: Number(p.scoreAway) }
          : { resultPick: null, scoreHome: 0, scoreAway: 0 }
      })
      this.drafts = drafts
    },
    selectDate (d) {
      this.selectedDate = d
      this.loadEligibility(d)
    },
    async loadEligibility (matchDate, force) {
      if (!force && this.eligByDate[matchDate]) return
      this.eligLoading = true
      try {
        const checkFn = firebase.app().functions('asia-east2').httpsCallable('checkWorldCupEligibility')
        const result = await checkFn({ userkey: this.user.key, matchDate })
        this.$set(this.eligByDate, matchDate, result.data)
      } catch (err) {
        console.error(err)
      } finally {
        this.eligLoading = false
      }
    },
    predictionDeadline (m) {
      const matchDate = m.matchDate || ((m.kickoff || '').length >= 10 ? m.kickoff.substring(0, 10) : '')
      if (!matchDate) return m.kickoff || ''
      return moment.tz(matchDate, 'YYYY-MM-DD', 'Asia/Bangkok').subtract(1, 'day').format('YYYY-MM-DD') + ' 23:59:59'
    },
    isLocked (m) {
      return this.predictionDeadline(m) < this.nowStr
    },
    isDirty (m) {
      const d = this.drafts[m.id]
      if (!d || !d.resultPick) return false
      const p = this.myPredictions[m.id]
      if (!p) return true
      return p.resultPick !== d.resultPick || Number(p.scoreHome) !== d.scoreHome || Number(p.scoreAway) !== d.scoreAway
    },
    isContradiction (m) {
      const d = this.drafts[m.id]
      if (!d || !d.resultPick) return false
      const diff = d.scoreHome - d.scoreAway
      if (d.resultPick === '1') return diff <= 0
      if (d.resultPick === '2') return diff >= 0
      return diff !== 0
    },
    setPick (m, pick) {
      if (!this.canPredict || this.isLocked(m)) return
      const d = this.drafts[m.id]
      this.$set(this.drafts, m.id, { ...d, resultPick: pick })
    },
    stepScore (m, field, delta) {
      if (!this.canPredict || this.isLocked(m)) return
      const d = this.drafts[m.id]
      const val = Math.max(0, Math.min(15, (d[field] || 0) + delta))
      this.$set(this.drafts, m.id, { ...d, [field]: val })
    },
    shortName (name) {
      return (name || '').length > 10 ? (name || '').substring(0, 9) + '…' : name
    },
    pickLabel (m, pick) {
      if (pick === '1') return `${m.team1} ชนะ`
      if (pick === '2') return `${m.team2} ชนะ`
      return 'เสมอ'
    },
    pointsClass (points) {
      if (points >= 4) return 'chip-exact'
      if (points >= 1) return 'chip-result'
      return 'chip-zero'
    },
    formatKickoff (kickoff) {
      return moment.tz(kickoff, 'YYYY-MM-DD HH:mm:ss', 'Asia/Bangkok').format('HH:mm น.')
    },
    formatThaiDate (d) {
      if (!d) return '-'
      const thaiMonths = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']
      const m = moment.tz(d, 'YYYY-MM-DD', 'Asia/Bangkok')
      return `${m.date()} ${thaiMonths[m.month()]}`
    },
    formatTabDay (d) {
      const today = this.nowStr.substring(0, 10)
      const tomorrow = moment.tz(today, 'YYYY-MM-DD', 'Asia/Bangkok').add(1, 'day').format('YYYY-MM-DD')
      if (d === today) return 'วันนี้'
      if (d === tomorrow) return 'พรุ่งนี้'
      const thaiDays = ['อา.', 'จ.', 'อ.', 'พ.', 'พฤ.', 'ศ.', 'ส.']
      return thaiDays[moment.tz(d, 'YYYY-MM-DD', 'Asia/Bangkok').day()]
    },
    formatTabDate (d) {
      return moment.tz(d, 'YYYY-MM-DD', 'Asia/Bangkok').format('D/M')
    },
    async submitPicks () {
      if (this.submitting) return
      const picks = this.dirtyMatchIds.map((id) => ({
        matchId: id,
        resultPick: this.drafts[id].resultPick,
        scoreHome: this.drafts[id].scoreHome,
        scoreAway: this.drafts[id].scoreAway
      }))
      if (!picks.length) return
      this.submitting = true
      try {
        const submitFn = firebase.app().functions('asia-east2').httpsCallable('submitWorldCupPredictions')
        const result = await submitFn({ userkey: this.user.key, code: this.user.code, picks })
        const saved = result.data.saved || []
        const rejected = result.data.rejected || []
        const now = moment().tz('Asia/Bangkok').format('YYYY-MM-DD HH:mm:ss')
        saved.forEach((id) => {
          const d = this.drafts[id]
          const prev = this.myPredictions[id] || {}
          this.$set(this.myPredictions, id, {
            ...prev,
            matchId: id,
            resultPick: d.resultPick,
            scoreHome: d.scoreHome,
            scoreAway: d.scoreAway,
            updatedate: now
          })
        })
        if (saved.length) {
          this.$swal({
            icon: 'success',
            title: 'บันทึกคำทายแล้ว!',
            text: `บันทึก ${saved.length} คู่เรียบร้อย แก้ไขได้ถึง 23:59 ของวันก่อนแข่ง`
          })
        }
        if (rejected.length) {
          const reasons = rejected.map((r) => this.rejectReason(r)).join('\n')
          this.$swal({ icon: 'warning', title: 'บางคู่บันทึกไม่ได้', text: reasons })
        }
      } catch (err) {
        let msg = 'เกิดข้อผิดพลาด ลองใหม่อีกครั้ง'
        if (err.code === 'failed-precondition') {
          msg = (err.message === 'not-eligible')
            ? 'ยอดฝากไม่ถึงเกณฑ์ของชุดนี้' : 'คู่ที่เลือกหมดเวลาทายแล้ว'
          this.loadEligibility(this.selectedDate, true)
        }
        this.$swal({ icon: 'error', title: 'ไม่สำเร็จ', text: msg })
      } finally {
        this.submitting = false
        this.nowStr = moment().tz('Asia/Bangkok').format('YYYY-MM-DD HH:mm:ss')
      }
    },
    rejectReason (r) {
      const m = this.matches.find(x => x.id === r.matchId)
      const name = m ? `${m.team1} vs ${m.team2}` : r.matchId
      if (r.reason === 'closed' || r.reason === 'kicked-off') return `${name}: หมดเวลาทายแล้ว`
      if (r.reason === 'not-eligible') return `${name}: ยอดฝากไม่ถึง (${r.totalDeposit}/${r.required})`
      if (r.reason === 'finished') return `${name}: จบไปแล้ว`
      return `${name}: ทายไม่ได้`
    }
  }
}
</script>

<style scoped>
.worldcup-page {
  min-height: 100vh;
  padding-bottom: 90px;
}

/* ===== HERO ===== */
.wc-hero {
  position: relative;
  overflow: hidden;
  margin: 12px auto;
  width: calc(100% - 24px);
  max-width: 880px;
  border-radius: 24px;
  border: 2px solid rgba(var(--wc-primary-rgb),0.35);
  box-shadow: 0 8px 40px rgba(var(--wc-primary-rgb),0.22), inset 0 0 60px rgba(0,0,0,0.2);
}
.wc-hero-bg {
  position: absolute; inset: 0;
  background:
    radial-gradient(ellipse at 50% -20%, rgba(var(--wc-primary-rgb),0.26), transparent 55%),
    linear-gradient(160deg, var(--wc-primary) 0%, var(--wc-secondary) 35%, var(--wc-deep) 70%, var(--wc-secondary) 110%);
}
.wc-pitch {
  position: absolute; inset: 0;
  background: repeating-linear-gradient(90deg, rgba(255,255,255,0.045) 0 60px, transparent 60px 120px);
}
.wc-pitch::before {
  content: '';
  position: absolute;
  left: 50%; top: 50%;
  width: 210px; height: 210px;
  transform: translate(-50%, -50%);
  border: 2px solid rgba(255,255,255,0.10);
  border-radius: 50%;
}
.wc-pitch::after {
  content: '';
  position: absolute;
  left: 0; right: 0; top: 50%;
  border-top: 2px solid rgba(255,255,255,0.10);
}
.wc-glow {
  position: absolute;
  width: 260px; height: 260px;
  border-radius: 50%;
  filter: blur(60px);
  opacity: 0.55;
  pointer-events: none;
}
.wc-glow-1 { background: rgba(var(--wc-primary-rgb),0.35); top: -120px; left: -60px; }
.wc-glow-2 { background: rgba(var(--wc-secondary-rgb),0.30); bottom: -140px; right: -60px; }
.wc-hero-content {
  position: relative;
  text-align: center;
  padding: 26px 16px 20px;
}
.wc-hero-icon {
  font-size: 46px;
  display: flex;
  justify-content: center;
  gap: 6px;
}
.hero-ball {
  display: inline-block;
  animation: ballBounce 2.2s ease-in-out infinite;
  filter: drop-shadow(0 6px 10px rgba(0,0,0,0.45));
}
.hero-trophy {
  display: inline-block;
  animation: trophyFloatWc 3s ease-in-out infinite;
  filter: drop-shadow(0 0 14px rgba(var(--wc-primary-rgb),0.55));
}
@keyframes ballBounce { 0%,100% { transform: translateY(0) rotate(0deg); } 50% { transform: translateY(-10px) rotate(18deg); } }
@keyframes trophyFloatWc { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
.wc-hero-title {
  font-size: 30px;
  font-weight: 900;
  letter-spacing: 0.5px;
  background: linear-gradient(135deg, var(--wc-primary) 10%, var(--wc-accent) 40%, var(--wc-primary) 70%, var(--wc-primary));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 6px 0 4px;
  filter: drop-shadow(0 2px 12px rgba(var(--wc-primary-rgb),0.30));
}
.wc-hero-sub {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 2px;
  color: rgba(255,255,255,0.65);
  margin-bottom: 14px;
}
.wc-my-points {
  display: inline-flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  background: linear-gradient(160deg, rgba(0,0,0,0.35), rgba(0,60,30,0.35));
  border: 1.5px solid rgba(var(--wc-primary-rgb),0.55);
  border-radius: 18px;
  padding: 10px 34px;
  cursor: pointer;
  box-shadow: 0 4px 24px rgba(0,0,0,0.35), inset 0 0 24px rgba(var(--wc-primary-rgb),0.08);
}
.wc-my-points::after {
  content: '';
  position: absolute;
  top: 0; left: -80%;
  width: 50%; height: 100%;
  background: linear-gradient(100deg, transparent, rgba(255,255,255,0.18), transparent);
  animation: shineSweep 3.2s ease-in-out infinite;
}
@keyframes shineSweep { 0%, 55% { left: -80%; } 100% { left: 130%; } }
.pts-label { font-size: 11px; color: rgba(255,255,255,0.6); font-weight: 600; }
.pts-value { font-size: 28px; font-weight: 900; color: var(--wc-primary); line-height: 1.1; }
.pts-link { font-size: 11px; color: var(--wc-secondary); font-weight: 700; margin-top: 2px; }
.wc-rules {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: center;
  margin-top: 14px;
}
.rule-chip {
  font-size: 11px;
  font-weight: 700;
  color: rgba(255,255,255,0.85);
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.15);
  border-radius: 20px;
  padding: 4px 12px;
}

/* ===== LOADING / EMPTY ===== */
.wc-loading {
  text-align: center;
  color: rgba(255,255,255,0.6);
  padding: 50px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}
.loading-spinner {
  width: 32px; height: 32px;
  border: 3px solid rgba(255,255,255,0.1);
  border-top-color: var(--wc-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.wc-empty { text-align: center; padding: 50px 20px; }
.empty-icon { font-size: 48px; margin-bottom: 12px; }
.empty-text { color: #fff; font-size: 18px; font-weight: 700; }
.empty-sub { color: rgba(255,255,255,0.5); font-size: 13px; margin-top: 4px; }

/* ===== DAY TABS ===== */
.day-tabs {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 2px 12px 6px;
  -webkit-overflow-scrolling: touch;
  max-width: 880px;
  margin: 0 auto;
}
.day-tab {
  flex: 0 0 auto;
  min-width: 70px;
  border: 1.5px solid rgba(255,255,255,0.16);
  background: linear-gradient(180deg, rgba(20,34,60,0.85), rgba(8,16,36,0.9));
  border-radius: 16px;
  padding: 9px 14px;
  color: rgba(255,255,255,0.65);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: transform 0.15s, box-shadow 0.15s;
}
.day-tab.active {
  background: linear-gradient(160deg, #16a14a, var(--wc-secondary));
  border-color: var(--wc-primary);
  color: #fff;
  box-shadow: 0 4px 18px rgba(0,220,110,0.45), inset 0 0 14px rgba(255,255,255,0.08);
  transform: translateY(-2px);
}
.tab-day { font-size: 13px; font-weight: 800; }
.tab-date { font-size: 11px; opacity: 0.75; margin-top: 1px; }

/* ===== ELIGIBILITY BANNER ===== */
.elig-banner {
  margin: 10px auto;
  width: calc(100% - 24px);
  max-width: 880px;
  border-radius: 16px;
  padding: 12px 16px;
  font-size: 13px;
}
.elig-loading {
  background: rgba(255,255,255,0.06);
  color: rgba(255,255,255,0.6);
  text-align: center;
}
.elig-ok {
  background: linear-gradient(135deg, rgba(0,160,80,0.18), rgba(0,120,60,0.1));
  border: 1.5px solid rgba(80,220,140,0.4);
  color: #aef5cd;
  font-weight: 700;
  text-align: center;
}
.elig-no {
  background: linear-gradient(135deg, rgba(255,140,0,0.20), rgba(120,40,0,0.16));
  border: 1.5px solid rgba(255,170,60,0.5);
  color: #fff;
  box-shadow: 0 4px 18px rgba(255,120,0,0.15);
}
.elig-title { font-weight: 800; font-size: 14px; }
.elig-desc { color: rgba(255,255,255,0.8); margin-top: 4px; }
.elig-desc strong { color: var(--wc-primary); }
.elig-actions { display: flex; gap: 8px; margin-top: 10px; }
.btn-deposit {
  flex: 1;
  font-size: 14px; font-weight: 800;
  padding: 10px;
  border: none; border-radius: 12px;
  background: linear-gradient(135deg, var(--wc-primary), #FF8C00);
  color: #1a1a1a; cursor: pointer;
}
.btn-recheck {
  flex: 1;
  font-size: 14px; font-weight: 700;
  padding: 10px;
  border: 1.5px solid rgba(255,255,255,0.3);
  border-radius: 12px;
  background: rgba(255,255,255,0.08);
  color: #fff; cursor: pointer;
}
.elig-expired { color: rgba(255,255,255,0.55); font-size: 12px; margin-top: 6px; }

/* ===== MATCH CARDS ===== */
.match-list { padding: 2px 12px; max-width: 880px; margin: 0 auto; }
.match-card {
  position: relative;
  overflow: hidden;
  background:
    linear-gradient(155deg, rgba(10,60,30,0.5), rgba(6,14,34,0.93) 45%),
    repeating-linear-gradient(90deg, rgba(255,255,255,0.02) 0 40px, transparent 40px 80px);
  border: 1.5px solid rgba(120,255,170,0.18);
  border-radius: 20px;
  padding: 14px 16px;
  margin-bottom: 14px;
  box-shadow: 0 6px 24px rgba(0,0,0,0.35);
}
.card-finished { border-color: rgba(var(--wc-primary-rgb),0.35); }
.card-locked { opacity: 0.88; }
.match-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
.match-round {
  font-size: 11px;
  font-weight: 800;
  color: #032012;
  background: linear-gradient(135deg, #7dffb0, #1db35e);
  padding: 3px 12px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,200,100,0.35);
}
.match-time {
  font-size: 12px;
  color: var(--wc-primary);
  font-weight: 800;
  background: rgba(255,215,0,0.10);
  border: 1px solid rgba(255,215,0,0.3);
  padding: 3px 12px;
  border-radius: 12px;
}
.match-teams {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.team { flex: 1; text-align: center; min-width: 0; }
.team-flag {
  font-size: 38px;
  line-height: 1;
  width: 68px;
  height: 68px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(circle at 35% 30%, rgba(255,255,255,0.16), rgba(255,255,255,0.04) 70%);
  border: 2px solid rgba(255,255,255,0.18);
  border-radius: 50%;
  box-shadow: 0 4px 16px rgba(0,0,0,0.35), inset 0 0 18px rgba(255,255,255,0.05);
}
.team-name {
  font-size: 14px;
  font-weight: 800;
  color: #fff;
  margin-top: 7px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.match-center { flex: 0 0 auto; min-width: 64px; text-align: center; }
.vs-text {
  width: 44px;
  height: 44px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 900;
  font-style: italic;
  color: var(--wc-primary);
  background: linear-gradient(160deg, rgba(255,215,0,0.12), rgba(255,140,0,0.06));
  border: 1.5px solid rgba(255,215,0,0.4);
  border-radius: 50%;
  box-shadow: 0 0 16px rgba(255,200,0,0.18);
}
.final-score {
  font-size: 24px;
  font-weight: 900;
  color: var(--wc-primary);
  text-shadow: 0 0 10px rgba(255,215,0,0.3);
}

/* my result line */
.my-result {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed rgba(255,255,255,0.12);
  flex-wrap: wrap;
}
.my-pick-text { font-size: 12px; color: rgba(255,255,255,0.75); }
.no-pick { font-size: 12px; color: rgba(255,255,255,0.4); }
.points-chip {
  font-size: 12px;
  font-weight: 800;
  padding: 3px 12px;
  border-radius: 12px;
}
.chip-exact { background: linear-gradient(135deg, var(--wc-primary), #FF8C00); color: #1a1a1a; }
.chip-result { background: rgba(80,220,140,0.2); border: 1px solid rgba(80,220,140,0.5); color: #aef5cd; }
.chip-zero { background: rgba(255,255,255,0.08); color: rgba(255,255,255,0.45); }
.chip-wait { background: rgba(0,150,255,0.15); border: 1px solid rgba(0,150,255,0.4); color: var(--wc-secondary); }

/* pick UI */
.pick-area { margin-top: 10px; }
.pick-disabled { opacity: 0.45; pointer-events: none; }
.pick-row { display: flex; gap: 6px; }
.pick-btn {
  flex: 1;
  font-size: 13px;
  font-weight: 800;
  padding: 12px 6px;
  border: 1.5px solid rgba(255,255,255,0.30);
  border-radius: 14px;
  background: linear-gradient(180deg, rgba(255,255,255,0.13), rgba(255,255,255,0.05));
  color: #fff;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: transform 0.12s, box-shadow 0.12s, border-color 0.12s;
  box-shadow: 0 2px 10px rgba(0,0,0,0.25);
}
.pick-btn:hover { transform: translateY(-1px); border-color: rgba(255,255,255,0.5); }
.pick-btn:active { transform: scale(0.97); }
.pick-draw { flex: 0.6; }
.pick-btn.selected {
  background: linear-gradient(160deg, #1fc25e, #0a8d3a);
  border-color: #aef5cd;
  color: #fff;
  box-shadow: 0 4px 18px rgba(0,220,110,0.55), inset 0 0 12px rgba(255,255,255,0.15);
}
.score-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  flex-wrap: wrap;
}
.score-label { font-size: 12px; color: rgba(255,255,255,0.6); font-weight: 600; }
.stepper {
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(255,255,255,0.06);
  border: 1.5px solid rgba(255,255,255,0.15);
  border-radius: 12px;
  padding: 2px;
}
.step-btn {
  width: 30px; height: 30px;
  border: none; border-radius: 10px;
  background: rgba(255,255,255,0.1);
  color: #fff;
  font-size: 17px;
  font-weight: 800;
  cursor: pointer;
  line-height: 1;
}
.step-btn:active { background: rgba(255,255,255,0.25); }
.step-val {
  min-width: 22px;
  text-align: center;
  font-size: 16px;
  font-weight: 900;
  color: var(--wc-primary);
}
.score-dash { color: rgba(255,255,255,0.5); font-weight: 900; }
.contra-warn { font-size: 11px; color: #ffb74d; font-weight: 600; width: 100%; }
.saved-note { font-size: 11px; color: #aef5cd; margin-top: 8px; font-weight: 600; }

/* ===== SAVE BAR ===== */
.save-bar {
  position: fixed;
  left: 0; right: 0; bottom: 70px;
  z-index: 998;
  display: flex;
  justify-content: center;
  padding: 0 16px;
}
.btn-save {
  width: 100%;
  max-width: 420px;
  font-size: 16px;
  font-weight: 900;
  padding: 14px;
  border: none;
  border-radius: 50px;
  background: linear-gradient(135deg, var(--wc-primary), #FF8C00);
  color: #1a1a1a;
  cursor: pointer;
  box-shadow: 0 6px 24px rgba(255,140,0,0.5);
}
.btn-save:disabled { opacity: 0.6; }

/* ===== FOOTER ===== */
.wc-footer { text-align: center; padding: 20px 16px; }
.btn-back {
  font-size: 15px; font-weight: 700;
  padding: 12px 36px;
  border: 1.5px solid rgba(255,255,255,0.2);
  border-radius: 50px;
  background: linear-gradient(135deg, rgba(255,255,255,0.12), rgba(255,255,255,0.06));
  color: #fff; cursor: pointer;
  backdrop-filter: blur(8px);
}
.btn-back:active { background: rgba(255,255,255,0.18); }

@media (max-width: 380px) {
  .wc-hero-title { font-size: 22px; }
  .team-flag { font-size: 30px; width: 56px; height: 56px; }
  .pick-btn { font-size: 11px; padding: 10px 4px; }
}
</style>
